import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../services/api';

const HealingJobContext = createContext(null);

export const JOBS_PAGE_SIZE = 200;
export const INVERTED_RANGE_ERROR = '‘Created from’ is after ‘Created to’';
const LOAD_ERROR = "Couldn't load jobs; try Refresh";

/** The agent's 400 text (e.g. a bad date) when present, else a generic retry hint. */
const loadErrorMessage = (err) => (err?.status === 400 && err.message ? err.message : LOAD_ERROR);

/**
 * Provides healing-job data fetched from the hub, one page (200) at a time.
 * The created-date range is filtered on the server (setDateRange); loadMore
 * appends the next page. JobTrackerTab sets the range on mount, which loads
 * the first page. `error` is the last load failure, cleared by the next
 * successful load.
 */
export function HealingJobProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const rangeRef = useRef({ createdFrom: '', createdTo: '' });
  // Server offset of the next page (count of rows the server returned, before de-duplication).
  const offsetRef = useRef(0);
  // Bumped by every first-page load; a response from an older generation is dropped.
  const generationRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const fetchPage = (offset) => api.getHealJobs({ limit: JOBS_PAGE_SIZE, offset, ...rangeRef.current });

  const applyPageMeta = (pageItems, pageTotal) => {
    setTotal(pageTotal);
    setHasMore(pageTotal != null
      ? offsetRef.current < pageTotal
      : pageItems.length >= JOBS_PAGE_SIZE);
  };

  /** Empty the list so jobs from another range are never shown under the current one. */
  const clearJobs = (message) => {
    offsetRef.current = 0;
    setJobs([]);
    setTotal(null);
    setHasMore(false);
    setError(message);
  };

  /**
   * Load the first page for the current range. On failure, `keepJobs` keeps the
   * list (same range, e.g. Refresh); otherwise it is cleared.
   */
  const loadFirstPage = async (keepJobs) => {
    const generation = ++generationRef.current;
    const { createdFrom, createdTo } = rangeRef.current;
    if (createdFrom && createdTo && createdFrom > createdTo) {
      clearJobs(INVERTED_RANGE_ERROR);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { items, total: pageTotal } = await fetchPage(0);
      if (generation !== generationRef.current) return;
      offsetRef.current = items.length;
      const seen = new Set();
      setJobs(items.filter((j) => !seen.has(j.id) && seen.add(j.id)));
      applyPageMeta(items, pageTotal);
      setError(null);
    } catch (err) {
      if (generation !== generationRef.current) return;
      console.error('Failed to fetch healing jobs:', err);
      if (keepJobs) setError(loadErrorMessage(err));
      else clearJobs(loadErrorMessage(err));
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
  };

  /** Reload the first page for the current date range (drops later pages). */
  const refreshJobs = useCallback(() => loadFirstPage(true),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  /** Filter by created date on the server ('YYYY-MM-DD' inclusive UTC days, '' = open) and reload. */
  const setDateRange = useCallback(({ createdFrom = '', createdTo = '' } = {}) => {
    const next = { createdFrom: createdFrom || '', createdTo: createdTo || '' };
    const changed = next.createdFrom !== rangeRef.current.createdFrom
      || next.createdTo !== rangeRef.current.createdTo;
    rangeRef.current = next;
    return loadFirstPage(!changed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Append the next page; jobs already loaded (new arrivals shift offsets) are skipped. */
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const generation = generationRef.current;
    setLoadingMore(true);
    try {
      const offset = offsetRef.current;
      const { items, total: pageTotal } = await fetchPage(offset);
      if (generation !== generationRef.current) return;
      offsetRef.current = offset + items.length;
      setJobs((prev) => {
        const seen = new Set(prev.map((j) => j.id));
        return [...prev, ...items.filter((j) => !seen.has(j.id) && seen.add(j.id))];
      });
      applyPageMeta(items, pageTotal);
      // An agent without paging ignores offset and repeats page 1: stop offering more.
      if (pageTotal == null && items.length > 0) {
        const loaded = new Set(jobs.map((j) => j.id));
        if (items.every((j) => loaded.has(j.id))) setHasMore(false);
      }
      setError(null);
    } catch (err) {
      if (generation !== generationRef.current) return;
      console.error('Failed to load more healing jobs:', err);
      setError(loadErrorMessage(err));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  /** Poll external service for fresh status and update in-place. */
  const pollJob = useCallback(async (jobId) => {
    try {
      const updatedJob = await api.pollHealJob(jobId);
      setJobs((prev) => prev.map((j) => (j.id === jobId ? updatedJob : j)));
      return updatedJob;
    } catch (err) {
      console.error('Failed to poll job:', err);
      throw err;
    }
  }, []);

  /** Helper to replace a job in the flat list by id. */
  const _replaceJob = useCallback((jobId, updater) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? updater(j) : j)));
  }, []);

  const approveJob = useCallback(async (jobId, jiraTicket, editedConfig, editedParsedConfig) => {
    const data = await api.approveHealJob(jobId, {
      jiraTicket,
      editedConfig,
      editedParsedConfig,
    });
    _replaceJob(jobId, (j) => ({ ...j, status: data.status || 'completed', review_context: null }));
    return data;
  }, [_replaceJob]);

  const rejectJob = useCallback(async (jobId, reason, reasonCode) => {
    const data = await api.rejectHealJob(jobId, reason, reasonCode);
    // Keep review_context so the job stays expandable (and reopenable) in this session.
    _replaceJob(jobId, (j) => ({
      ...j,
      status: data.status || 'rejected',
      review_context: { ...(j.review_context || {}), rejected_at: new Date().toISOString() },
    }));
    return data;
  }, [_replaceJob]);

  /** Reopen a rejected job, then re-fetch it so the review actions appear. */
  const reopenJob = useCallback(async (jobId) => {
    const data = await api.reopenHealJob(jobId);
    try {
      await pollJob(jobId);
    } catch {
      // Poll failed: apply the reopen response locally so the panel shows
      // "Reopened by…" instead of stale rejection data.
      _replaceJob(jobId, (j) => {
        const {
          rejected_at: _rejectedAt,
          rejection_reason: _reason,
          rejection_reason_code: _reasonCode,
          ...rest
        } = j.review_context || {};
        return {
          ...j,
          status: 'awaiting_review',
          review_context: data.reopened ? { ...rest, reopened: data.reopened } : rest,
        };
      });
    }
    return data;
  }, [pollJob, _replaceJob]);

  const reviseJob = useCallback(async (jobId, feedback) => {
    const data = await api.reviseHealJob(jobId, feedback);
    _replaceJob(jobId, (j) => ({
      ...j,
      status: data.status || 'running',
      message: data.message || j.message,
      review_context: null,
    }));
    return data;
  }, [_replaceJob]);

  return (
    <HealingJobContext.Provider
      value={{
        jobs,
        total,
        hasMore,
        loading,
        loadingMore,
        error,
        refreshJobs,
        setDateRange,
        loadMore,
        pollJob,
        approveJob,
        rejectJob,
        reopenJob,
        reviseJob,
      }}
    >
      {children}
    </HealingJobContext.Provider>
  );
}

export function useHealingJobs() {
  const ctx = useContext(HealingJobContext);
  if (!ctx) {
    throw new Error('useHealingJobs must be used within a HealingJobProvider');
  }
  return ctx;
}
